import media from "@/config/media";

// Breakfast collection supplied by Urvashi. Nutrition values have not been costed or
// lab-checked yet, so they are left null rather than estimated.
// Every dish carries a Parent Note (what is in it and why) and a Kid Note
// (a fun, plain-language line for the child) as required by our standards.

export const breakfastIntro = {
  eyebrow: "Breakfast collection",
  title: "The mornings our grandmothers got right.",
  intro:
    "Traditional Indian breakfasts chosen for growing children — regional porridges, steamed fruit, sprouted millets and warm milk bowls, alongside a few modern favourites children genuinely ask for. Prepared fresh, portioned by age.",
  statusLabel: "Available now · breakfast plans open",
  note:
    "Breakfast can be taken on its own or alongside lunch, with home or school delivery in the areas we currently serve.",
};

export const breakfastGroups = [
  {
    id: "traditional",
    title: "Traditional & regional mornings",
    summary:
      "Recipes carried down through Kerala, Gujarat and South Indian households, cooked the way they were meant to be.",
  },
  {
    id: "power-bowls",
    title: "Warm power bowls",
    summary:
      "Slow-cooked ancient grains and milk bowls, portioned differently for younger and older children.",
  },
  {
    id: "modern-favourites",
    title: "Modern favourites",
    summary:
      "The plates children ask for by name, made with whole grains, real spreads and no shortcuts.",
  },
];

export const breakfastItems = [
  {
    id: "raab",
    group: "traditional",
    name: "Gujarati Raab",
    label: "Gujarat · warm wellness drink",
    tradition:
      "A comforting drink Gujarati grandmothers have made for generations, using whole wheat or millet flour with ghee, jaggery and warming spices such as dry ginger and carom seeds.",
    description:
      "A warm, drinkable porridge of millet or whole-wheat flour cooked with ghee, jaggery, dry ginger and carom seeds.",
    ingredients: [
      "Bajra or jowar flour (or whole wheat)",
      "Cow ghee",
      "Organic jaggery",
      "Dry ginger",
      "Carom seeds (ajwain)",
    ],
    parentNote:
      "Traditionally served when appetite or digestion needs a gentle nudge. Sipped warm rather than eaten with a spoon.",
    kidNote: "A warm, sweet, gingery drink for chilly mornings.",
    ages: ["2-4", "5-7", "8-10", "11-plus"],
    vegetarian: true,
    allergens: ["Milk (ghee)", "Wheat (gluten) if the wheat version is chosen"],
    image: media.breakfast.raab,
    storage: "Serve warm on delivery. Do not keep overnight.",
    reheating: "Warm gently on the stove, whisking, and thin with hot water if needed.",
    availability: "On the confirmed menu",
  },
  {
    id: "bajra-rice-kadha",
    group: "traditional",
    name: "Rice & Millet Milk Porridge",
    label: "Urvashi’s mother’s recipe",
    tradition:
      "Urvashi’s mother’s comforting morning porridge — bajra purée and rice boiled in milk with turmeric and ginger. Easy on the stomach and easy to feed.",
    description:
      "Bajra purée and rice simmered in milk with turmeric and fresh ginger until soft and creamy.",
    ingredients: ["Bajra (pearl millet)", "Rice", "Milk", "Turmeric", "Ginger"],
    parentNote:
      "Cooked long and soft, which is why it suits sensitive mornings and children who eat slowly.",
    kidNote: "Creamy golden porridge — soft enough to eat with your eyes still half closed.",
    ages: ["2-4", "5-7", "8-10", "11-plus"],
    vegetarian: true,
    allergens: ["Milk"],
    image: media.breakfast.bajraKadha,
    storage: "Best warm. Refrigerate below 5°C if kept and finish the same day.",
    reheating: "Add a little milk and warm gently, stirring.",
    availability: "On the confirmed menu",
  },
  {
    id: "saffron-almond-porridge",
    group: "power-bowls",
    name: "Gold-Crown Saffron & Almond Porridge",
    label: "Ages 2–7",
    tradition: null,
    description:
      "A silky-smooth, naturally sweet morning bowl crafted for little tummies. Toasted whole oats simmered in warm milk with crushed soaked almonds, golden saffron, a hint of nutmeg and chia seeds. Naturally sweetened with date syrup and topped with soft fresh fruits.",
    ingredients: [
      "Whole oats",
      "Milk",
      "Soaked almonds",
      "Saffron",
      "Nutmeg",
      "Chia seeds",
      "Date syrup",
      "Fresh fruit",
    ],
    parentNote:
      "Rich in bio-available iron, healthy saturated fats and gentle digestive spices to support skeletal growth and calm morning energy.",
    kidNote: "A golden bowl with soft fruit on top — it gives you steady get-up-and-go.",
    ages: ["2-4", "5-7"],
    vegetarian: true,
    allergens: ["Milk", "Tree nuts (almond)", "Oats (gluten)"],
    image: media.breakfast.saffronAlmond,
    storage: "Best warm. Keep the fruit topping chilled until serving.",
    reheating: "Loosen with warm milk and heat gently. Add the fruit after warming.",
    availability: "On the confirmed menu",
  },
  {
    id: "brain-power-oats",
    group: "power-bowls",
    name: "The Ultimate Brain-Power Oats Bowl",
    label: "Ages 8+",
    tradition: null,
    description:
      "A rich, aromatic breakfast bowl packed with crunch to fuel a full day of school and sport. Ghee-roasted oats cooked with Kashmiri saffron, almonds, chia and raisins, topped with fresh seasonal fruit, crisp walnuts and a drizzle of raw honey.",
    ingredients: [
      "Whole oats",
      "Cow ghee",
      "Kashmiri saffron",
      "Almonds",
      "Walnuts",
      "Chia seeds",
      "Raisins",
      "Raw honey",
      "Seasonal fruit",
    ],
    parentNote:
      "Contains Omega-3 (ALA) and L-carnitine to support brain cell health, memory retention and sustained focus.",
    kidNote: "Crunchy, nutty and a little sweet — breakfast for a big school day.",
    ages: ["8-10", "11-plus"],
    vegetarian: true,
    allergens: ["Milk (ghee)", "Tree nuts (almond, walnut)", "Oats (gluten)", "Honey"],
    image: media.breakfast.brainOats,
    storage: "Best warm. Keep fruit and honey aside until serving.",
    reheating: "Warm the oat base gently, then add the toppings.",
    availability: "On the confirmed menu",
  },
  {
    id: "rajgira-velvet",
    group: "power-bowls",
    name: "Golden Rajgira Velvet Bowl",
    label: "Ages 2–7",
    tradition:
      "Amaranth, or rajgira, is an ancient pseudograin used across Indian kitchens and naturally free from gluten.",
    description:
      "A silky, comforting ancient-grain porridge made for delicate bellies. Whole amaranth seeds slow-cooked with A2 ghee, warm milk, organic jaggery and green cardamom, finished with mashed banana and powdered nuts.",
    ingredients: [
      "Whole amaranth (rajgira)",
      "A2 cow ghee",
      "Milk",
      "Organic jaggery",
      "Green cardamom",
      "Banana",
      "Powdered nuts",
    ],
    parentNote:
      "100% gluten-free. A complete protein with high calcium to help build strong bones, teeth and muscles.",
    kidNote: "Velvety and warm with soft banana mixed through it.",
    ages: ["2-4", "5-7"],
    vegetarian: true,
    allergens: ["Milk", "Tree nuts"],
    image: media.breakfast.rajgiraBowl,
    storage: "Best warm. Refrigerate below 5°C if kept and finish the same day.",
    reheating: "Add a little warm milk and heat gently, stirring.",
    availability: "On the confirmed menu",
  },
  {
    id: "warrior-amaranth",
    group: "power-bowls",
    name: "The Ancient Warrior Amaranth Bowl",
    label: "Ages 8+",
    tradition:
      "The same ancient pseudograin, built up for older children with crunch and fresh fruit.",
    description:
      "A nutrient-dense super-grain breakfast bowl loaded with crunch and natural energy. A rich base of ghee-roasted amaranth cooked in cardamom milk, topped with crisp puffed rajgira, sliced fresh fruit, pumpkin seeds and a touch of wild honey.",
    ingredients: [
      "Amaranth (rajgira)",
      "Puffed rajgira",
      "A2 cow ghee",
      "Milk",
      "Green cardamom",
      "Pumpkin seeds",
      "Wild honey",
      "Fresh fruit",
    ],
    parentNote:
      "Packed with lysine-rich complete protein, bio-available iron and zinc to fuel school performance and sports stamina.",
    kidNote: "Crunchy puffs on top of a warm creamy bowl — proper fuel before a match.",
    ages: ["8-10", "11-plus"],
    vegetarian: true,
    allergens: ["Milk", "Honey", "Seeds (pumpkin)"],
    image: media.breakfast.warriorAmaranth,
    storage: "Best warm. Keep the puffs, fruit and honey aside until serving so the crunch holds.",
    reheating: "Warm the amaranth base gently, then add the toppings.",
    availability: "On the confirmed menu",
  },
  {
    id: "banana-pancakes",
    group: "modern-favourites",
    name: "Banana & Peanut Butter Pancakes",
    label: "Child favourite",
    tradition: null,
    description:
      "Soft banana pancakes with real peanut butter and a drizzle of honey or date syrup.",
    ingredients: ["Banana", "Whole-grain flour", "Milk", "Peanut butter", "Date syrup or honey"],
    parentNote:
      "Sweetened with fruit and date syrup rather than refined sugar. Peanut butter is included, so this dish is not suitable for children with a peanut allergy.",
    kidNote: "Pancakes. With banana and peanut butter. Enough said.",
    ages: ["2-4", "5-7", "8-10", "11-plus"],
    vegetarian: true,
    allergens: ["Peanuts", "Milk", "Gluten", "Honey (if chosen)"],
    image: media.breakfast.bananaPancakes,
    storage: "Best eaten warm on delivery.",
    reheating: "Warm on a dry pan for 30 seconds a side.",
    availability: "On the confirmed menu",
  },
  {
    id: "chia-oat-wrap",
    group: "modern-favourites",
    name: "Chia & Oat Wrap with Vegetables and Paneer",
    label: "Hand-held",
    tradition: null,
    description:
      "A soft chia and oat wrap stuffed with sautéed vegetables and paneer or cheese.",
    ingredients: ["Oats", "Chia seeds", "Whole-wheat flour", "Seasonal vegetables", "Paneer or cheese"],
    parentNote:
      "A hand-held option for mornings that are already running late. Vegetables are cooked soft and diced small.",
    kidNote: "A soft wrap you can hold in one hand, packed with cheese and veg.",
    ages: ["2-4", "5-7", "8-10", "11-plus"],
    vegetarian: true,
    allergens: ["Milk", "Wheat (gluten)", "Oats"],
    image: media.breakfast.chiaWrap,
    storage: "Best eaten warm. Refrigerate below 5°C if kept and finish the same day.",
    reheating: "Warm on a dry pan for a minute a side.",
    availability: "On the confirmed menu",
  },
  {
    id: "spinach-cheese-eggs",
    group: "modern-favourites",
    name: "Scrambled Spinach & Cheese Eggs with Multigrain Toast",
    label: "Contains egg",
    tradition: null,
    description:
      "Soft scrambled eggs folded with spinach and cheese, served with toasted multigrain bread.",
    ingredients: ["Eggs", "Spinach", "Cheese", "Butter", "Multigrain bread"],
    parentNote:
      "This is the only egg-based dish on the breakfast menu. It is offered separately so vegetarian households are never served egg by default.",
    kidNote: "Cheesy scrambled eggs with green bits — and toast to scoop them up.",
    ages: ["2-4", "5-7", "8-10", "11-plus"],
    vegetarian: false,
    allergens: ["Egg", "Milk", "Wheat (gluten)"],
    image: media.breakfast.spinachEggs,
    storage: "Eat fresh on delivery. Do not keep.",
    reheating: "Not recommended — eggs are best eaten as delivered.",
    availability: "On the confirmed menu · opt-in only",
  },
  {
    id: "moong-chilla",
    group: "modern-favourites",
    name: "Sprouted Mung Dal Chilla",
    label: "Sprouted protein",
    tradition: null,
    description:
      "A soft sprouted mung dal pancake with a creamy spread, stuffed with paneer or egg.",
    ingredients: ["Sprouted mung dal", "Paneer or egg", "Creamy herb spread", "Cow ghee"],
    parentNote:
      "Made from ground sprouted mung rather than flour. The paneer filling is fully vegetarian; the egg filling is opt-in and never sent unless you ask for it.",
    kidNote: "A savoury golden pancake with a creamy filling rolled inside.",
    ages: ["2-4", "5-7", "8-10", "11-plus"],
    vegetarian: true,
    eggOption: true,
    allergens: ["Milk", "Egg (if the egg filling is chosen)"],
    image: media.breakfast.moongChilla,
    storage: "Best eaten warm. Refrigerate below 5°C if kept and finish the same day.",
    reheating: "Warm on a dry pan for a minute a side.",
    availability: "Available",
  },
];

export default breakfastItems;
